// frontend/openaiModal.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Checkbox,
  Chip,
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  ExpandMore,
  Add,
  Delete,
  Info,
  Settings,
  Timer,
  Chat,
  SwapHoriz,
  PictureAsPdf
} from "@material-ui/icons";
import { InputAdornment } from "@material-ui/core";
// RAG desativado (2026-09-28): substituído por "Gerar prompt a partir de PDF".
// import AiKnowledgeFilesUpload from "../AiKnowledgeFilesUpload";
import AiPromptInstructionsModal from "../AiPromptInstructionsModal";
import ConfirmationModal from "../ConfirmationModal";
// import { MenuBook } from "@material-ui/icons"; // RAG desativado (2026-09-28)
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { Alert } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";

// Provedores e modelos vêm do backend (GET /llm-providers e, ao informar a
// API Key, POST /llm-providers/:id/models -- lista ao vivo do provedor), o
// mesmo padrão da tela de Agente de I.A. do G-Click.

// RAG desativado (2026-09-28). Só OpenAI e Gemini geram embeddings com a mesma key do nó --
// os demais provedores não suportavam a Base de Conhecimento.
// const KNOWLEDGE_BASE_PROVIDERS = ["openai", "gemini"];

// "Gerar a partir de PDF": o backend lê o PDF e devolve o texto do prompt
// no formato das Instruções (AiPromptInstructionsModal). Nada é salvo até o
// usuário salvar o nó e o fluxo.
const MAX_PROMPT_PDF_SIZE = 5 * 1024 * 1024; // mesmo limite do backend

// Códigos de erro de POST /ai-prompt/generate-from-pdf
// (AiPromptController / GeneratePromptFromPdfService / LLMCompletionService).
const PROMPT_FROM_PDF_ERRORS = {
  ERR_NO_FILE_UPLOADED: "Selecione um arquivo PDF.",
  ERR_FILE_MUST_BE_PDF: "O arquivo precisa ser um PDF.",
  ERR_PDF_TOO_LARGE: "O PDF deve ter no máximo 5 MB.",
  ERR_PDF_PASSWORD_PROTECTED: "O PDF está protegido por senha. Remova a senha e tente novamente.",
  ERR_PDF_UNREADABLE: "Não foi possível ler o PDF (arquivo corrompido ou inválido).",
  ERR_PDF_NO_TEXT: "O PDF não contém texto selecionável (parece ser uma imagem digitalizada).",
  ERR_PDF_TEXT_TOO_LONG: "O documento é longo demais para virar prompt. Divida ou resuma o conteúdo.",
  ERR_AI_PROMPT_MISSING_PARAMS: "Preencha provedor, API Key e modelo antes de gerar.",
  ERR_INVALID_LLM_PROVIDER: "Provedor inválido.",
  ERR_LLM_INVALID_KEY: "API Key inválida ou sem acesso a esse provedor.",
  ERR_LLM_NO_CREDITS: "A conta do provedor está sem créditos.",
  ERR_LLM_RATE_LIMITED: "Limite de uso do provedor atingido. Tente novamente em instantes.",
  ERR_LLM_MODEL_UNAVAILABLE: "Modelo indisponível para essa API Key.",
  ERR_LLM_CONTEXT_TOO_LONG: "O documento não cabe no modelo selecionado. Escolha outro modelo.",
  ERR_LLM_TIMEOUT: "A geração demorou demais. Tente um modelo mais rápido.",
  ERR_LLM_EMPTY_OUTPUT: "O modelo não retornou texto. Tente outro modelo (modelos de raciocínio podem falhar aqui).",
  ERR_LLM_PROVIDER_REQUEST_FAILED: "O provedor de IA retornou um erro. Tente novamente."
};

// Lista de vozes disponíveis para OpenAI
const availableVoices = [
  "texto",
  "pt-BR-FabioNeural",
  "pt-BR-FranciscaNeural",
  "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural",
  "pt-BR-DonatoNeural",
  "pt-BR-ElzaNeural",
  "pt-BR-GiovannaNeural",
  "pt-BR-HumbertoNeural",
  "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural",
  "pt-BR-LeticiaNeural",
  "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural",
  "pt-BR-ValerioNeural",
  "pt-BR-YaraNeural"
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  sectionTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  flowModeCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  temporarySettings: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px dashed ${theme.palette.primary.light}`,
    // `grey[50]` e fixo, entao esse bloco fica claro tambem no modo escuro e o
    // conteudo, que vem do tema escuro, sai quase branco em cima de quase
    // branco. So no escuro o conteudo volta a ser escuro e os titulos ficam no
    // laranja da marca. No claro nada disso e emitido.
    ...(theme.palette.type === "dark" && {
      // Depois do atalho `border` de proposito: o laranja do tracejado tambem
      // fica fraco em cima do fundo claro, entao no escuro ele vira preto.
      borderColor: theme.palette.common.black,
      "& .MuiTypography-root:not(.MuiTypography-colorError), & .MuiFormLabel-root:not(.Mui-error), & .MuiInputBase-root, & .MuiSelect-icon, & .MuiCheckbox-root, & .MuiChip-root, & .MuiButton-root": {
        color: theme.palette.common.black
      },
      "& .MuiOutlinedInput-notchedOutline, & .MuiChip-outlined": {
        borderColor: "rgba(0, 0, 0, 0.23)"
      },
      // Depois do grupo de cima de proposito: mesma especificidade, ganha a
      // ultima regra, entao o titulo fica laranja e o resto preto.
      "& .MuiTypography-h6": {
        color: theme.palette.primary.main
      }
    })
  },
  keywordChip: {
    margin: theme.spacing(0.5),
  },
  helpText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    }
  },
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column"
  }
}));

// Esquema de validação para OpenAI
const OpenAiSchema = Yup.object().shape({
  name: Yup.string()
    .min(5, "Muito curto!")
    .max(100, "Muito longo!")
    .required("Obrigatório"),
  prompt: Yup.string()
    .min(50, "Muito curto!")
    .required("Descreva o treinamento para a IA, ou gere a partir de um PDF"),
  // RAG desativado (2026-09-28): regra "prompt OU PDF pronto na Base de Conhecimento".
  // prompt: Yup.string().when(
  //   ["knowledgeEnabled", "hasReadyKnowledgeFile"],
  //   {
  //     is: (knowledgeEnabled, hasReadyKnowledgeFile) =>
  //       !(knowledgeEnabled && hasReadyKnowledgeFile),
  //     then: Yup.string()
  //       .min(50, "Muito curto!")
  //       .required("Descreva o treinamento para a IA, ou anexe um PDF na Base de Conhecimento"),
  //     otherwise: Yup.string().notRequired()
  //   }
  // ),
  provider: Yup.string().required("Selecione o provedor"),
  model: Yup.string().required("Selecione o modelo"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(4000, "Máximo 4000 tokens")
    .required("Informe o número máximo de tokens"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(2, "Máximo 2")
    .required("Informe a temperatura"),
  apiKey: Yup.string().required("Informe a API Key"),
  maxMessages: Yup.number()
    .min(1, "Mínimo 1 mensagem")
    .max(100, "Máximo 100 mensagens")
    .required("Informe o número máximo de mensagens"),
  voice: Yup.string().required("Informe o modo para Voz"),
  voiceKey: Yup.string().when("voice", {
    is: (voice) => voice !== "texto",
    then: Yup.string().required("Voice Key é obrigatória para síntese de voz"),
    otherwise: Yup.string().notRequired()
  }),
  voiceRegion: Yup.string().when("voice", {
    is: (voice) => voice !== "texto",
    then: Yup.string().required("Voice Region é obrigatória para síntese de voz"),
    otherwise: Yup.string().notRequired()
  }),
  flowMode: Yup.string()
    .oneOf(["permanent", "temporary"], "Modo de fluxo inválido")
    .required("Selecione o modo de fluxo"),
  maxInteractions: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      .min(1, "Mínimo 1 interação")
      .max(50, "Máximo 50 interações")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  completionTimeout: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      .min(1, "Mínimo 1 minuto")
      .max(60, "Máximo 60 minutos")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  continueKeywords: Yup.array().when("flowMode", {
    is: "temporary",
    then: Yup.array()
      .of(Yup.string().required("Palavra-chave não pode estar vazia"))
      .min(1, "Pelo menos uma palavra-chave é obrigatória no modo temporário"),
    otherwise: Yup.array()
  }),
  objective: Yup.string().when(["flowMode", "autoCompleteOnObjective"], {
    is: (flowMode, autoComplete) => flowMode === "temporary" && autoComplete,
    then: Yup.string().required("Objetivo é obrigatório quando auto completar está ativo"),
    otherwise: Yup.string()
  })
});

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close, flowId }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    prompt: "",
    provider: "openai",
    model: "",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    maxTokens: 1000,
    temperature: 0.7,
    apiKey: "",
    maxMessages: 10,
    queueId: 0,
    
    // Campos para controle de fluxo
    flowMode: "permanent",
    maxInteractions: 5,
    completionTimeout: 10,
    continueKeywords: ["continuar", "próximo", "avançar"],
    objective: "",
    autoCompleteOnObjective: false,
    // RAG desativado (2026-09-28)
    // knowledgeEnabled: false,
    // hasReadyKnowledgeFile: false
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [labels, setLabels] = useState({
    title: "Adicionar AI Assistant ao fluxo",
    btn: "Adicionar",
  });
  const [newKeyword, setNewKeyword] = useState("");
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(false);
  const [queues, setQueues] = useState([]);

  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const formikRef = useRef(null);

  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [promptGenError, setPromptGenError] = useState("");
  const [promptGenInfo, setPromptGenInfo] = useState(null);
  const [pendingPromptPdf, setPendingPromptPdf] = useState(null);
  const pdfInputRef = useRef(null);
  // Cancela a geração em andamento se o modal fechar; também identifica a
  // requisição atual para descartar respostas que cheguem atrasadas.
  const promptGenCancelRef = useRef(null);

  const cancelPromptGeneration = () => {
    if (promptGenCancelRef.current) {
      promptGenCancelRef.current.cancel();
      promptGenCancelRef.current = null;
    }
    setGeneratingPrompt(false);
  };

  useEffect(() => () => cancelPromptGeneration(), []);
  const modelsLoadedForRef = useRef(null);
  const latestRequestRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: queuesData }, { data: providersData }] = await Promise.all([
          api.get("/queue"),
          api.get("/llm-providers")
        ]);
        setQueues(queuesData);
        setProviders(providersData);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Mesma lógica de GclickAiAgent.maybeLoadModels: busca só quando a
  // combinação provedor+key muda, e descarta resposta obsoleta (troca rápida
  // de provedor). Se o modelo já escolhido não vier na lista nova, limpa o
  // campo pra forçar uma escolha válida; se a busca falhar, mantém o que
  // estava (o nó salvo continua exibindo o modelo dele).
  const loadModels = async (provider, apiKey) => {
    if (!provider || !apiKey) {
      modelsLoadedForRef.current = null;
      latestRequestRef.current = null;
      setModels([]);
      setModelsError("");
      return;
    }

    const comboKey = `${provider}|${apiKey}`;
    if (modelsLoadedForRef.current === comboKey) return;

    latestRequestRef.current = comboKey;
    setModelsLoading(true);
    setModelsError("");
    setModels([]);
    try {
      const { data } = await api.post(`/llm-providers/${provider}/models`, { apiKey });
      if (latestRequestRef.current !== comboKey) return;
      setModels(data);
      modelsLoadedForRef.current = comboKey;
      const currentModel = formikRef.current?.values?.model;
      if (currentModel && !data.some(m => m.id === currentModel)) {
        formikRef.current.setFieldValue("model", "");
      }
    } catch (err) {
      if (latestRequestRef.current !== comboKey) return;
      setModelsError(
        err?.response?.data?.error === "ERR_LLM_INVALID_KEY"
          ? "Chave inválida ou sem acesso a esse provedor."
          : "Não foi possível buscar os modelos desse provedor agora."
      );
    } finally {
      if (latestRequestRef.current === comboKey) setModelsLoading(false);
    }
  };

  useEffect(() => {
    setPromptGenError("");
    setPromptGenInfo(null);
    if (open === "edit") {
      setLabels({
        title: "Editar AI Assistant",
        btn: "Salvar",
      });
      const typebotIntegration = data?.data?.typebotIntegration || {};
      // Nós antigos não gravavam provider -- mesma dedução do backend
      // (OpenAiService.handleOpenAiFlow).
      const provider = typebotIntegration.provider
        || (typebotIntegration.model?.startsWith("gpt-") ? "openai" : "gemini");
      modelsLoadedForRef.current = null;
      loadModels(provider, typebotIntegration.apiKey);
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        provider,
        model: typebotIntegration.model || "",
        flowMode: typebotIntegration.flowMode || "permanent",
        continueKeywords: typebotIntegration.continueKeywords || ["continuar", "próximo", "avançar"],
        maxInteractions: typebotIntegration.maxInteractions || 5,
        completionTimeout: typebotIntegration.completionTimeout || 10,
        objective: typebotIntegration.objective || "",
        autoCompleteOnObjective: typebotIntegration.autoCompleteOnObjective || false,
        // knowledgeEnabled: typebotIntegration.knowledgeEnabled || false // RAG desativado (2026-09-28)
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar AI Assistant ao fluxo",
        btn: "Adicionar",
      });
      setIntegration(initialState);
      loadModels(null, null);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    cancelPromptGeneration();
    setPendingPromptPdf(null);
    setNewKeyword("");
    close(null);
  };

  const handleCancel = async () => {
    // RAG desativado (2026-09-28): limpeza de PDFs enviados para um nó novo não salvo.
    // if (open === "create" && data?.id) {
    //   try {
    //     const { data: orphanFiles } = await api.get("/ai-knowledge", {
    //       params: { flowId, nodeId: data.id },
    //     });
    //     await Promise.all(
    //       orphanFiles.map((file) => api.delete(`/ai-knowledge/${file.id}`))
    //     );
    //   } catch (err) {
    //     console.warn("Falha ao limpar arquivos de conhecimento não salvos", err);
    //   }
    // }
    handleClose();
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const { hasReadyKnowledgeFile, ...restValues } = values;
    const promptData = {
      ...restValues,
      id: open === "create" ? data?.id : restValues.id,
      // Garantir que campos do modo temporário sejam nulos se modo for permanente
      maxInteractions: values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout: values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords: values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective: values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
      // provider vem do seletor "Provedor" do formulário (openai/gemini) —
      // Gemini sempre responde em texto (mesma regra do modal Gemini
      // dedicado), então força voz="texto" nesse caso.
      voice: values.provider === "gemini" ? "texto" : values.voice,
      // knowledgeEnabled: KNOWLEDGE_BASE_PROVIDERS.includes(values.provider) && values.knowledgeEnabled // RAG desativado (2026-09-28)
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { typebotIntegration: promptData },
      });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({
        typebotIntegration: promptData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const generatePromptFromPdf = async (file) => {
    const { provider, apiKey, model } = formikRef.current?.values || {};
    if (!provider || !apiKey || !model) {
      setPromptGenError(PROMPT_FROM_PDF_ERRORS.ERR_AI_PROMPT_MISSING_PARAMS);
      return;
    }

    const source = axios.CancelToken.source();
    promptGenCancelRef.current = source;
    setGeneratingPrompt(true);
    setPromptGenError("");
    setPromptGenInfo(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("provider", provider);
    formData.append("apiKey", apiKey);
    formData.append("model", model);

    try {
      const { data: result } = await api.post("/ai-prompt/generate-from-pdf", formData, {
        cancelToken: source.token
      });
      if (promptGenCancelRef.current !== source) return;
      formikRef.current.setFieldValue("prompt", result.prompt);
      setPromptGenInfo({
        truncated: Boolean(result.truncated),
        placeholders: (result.prompt.match(/\[a definir\]/gi) || []).length
      });
    } catch (err) {
      if (axios.isCancel(err) || promptGenCancelRef.current !== source) return;
      const code = err?.response?.data?.error;
      setPromptGenError(
        PROMPT_FROM_PDF_ERRORS[code] ||
          "Falha ao gerar o prompt. Verifique a conexão e tente novamente."
      );
    } finally {
      if (promptGenCancelRef.current === source) {
        promptGenCancelRef.current = null;
        setGeneratingPrompt(false);
      }
    }
  };

  const handlePromptPdfSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    // Limpa o input para permitir escolher o mesmo arquivo de novo.
    e.target.value = "";
    if (!file) return;

    setPromptGenError("");
    setPromptGenInfo(null);
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setPromptGenError(PROMPT_FROM_PDF_ERRORS.ERR_FILE_MUST_BE_PDF);
      return;
    }
    if (file.size > MAX_PROMPT_PDF_SIZE) {
      setPromptGenError(PROMPT_FROM_PDF_ERRORS.ERR_PDF_TOO_LARGE);
      return;
    }

    // Nunca sobrescreve um prompt já escrito sem confirmação.
    if (formikRef.current?.values?.prompt?.trim()) {
      setPendingPromptPdf(file);
      return;
    }
    generatePromptFromPdf(file);
  };

  const addKeyword = (arrayHelpers, keyword) => {
    if (keyword.trim() && !integration.continueKeywords.includes(keyword.trim())) {
      arrayHelpers.push(keyword.trim());
      setNewKeyword("");
    }
  };

  const removeKeyword = (arrayHelpers, index) => {
    arrayHelpers.remove(index);
  };

  const getVoiceDisplayName = (voice) => {
    if (voice === "texto") return "Apenas Texto";
    return voice.replace("pt-BR-", "").replace("Neural", "");
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleCancel}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {labels.title}
        </DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          innerRef={formikRef}
          validationSchema={OpenAiSchema}
          onSubmit={handleSavePrompt}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue, handleBlur }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                
                {/* CONFIGURAÇÕES BÁSICAS */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Configurações Básicas - AI Assistant
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>

                    <Field
                      as={TextField}
                      label="Nome do Assistente"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                    />

                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.provider && Boolean(errors.provider)}
                    >
                      <InputLabel>Provedor *</InputLabel>
                      <Select
                        label="Provedor *"
                        name="provider"
                        value={values.provider}
                        onChange={(e) => {
                          const newProvider = e.target.value;
                          setFieldValue("provider", newProvider);
                          // O modelo selecionado só faz sentido para o
                          // provedor anterior — limpa e busca a lista do novo.
                          setFieldValue("model", "");
                          if (newProvider === "gemini") {
                            setFieldValue("voice", "texto");
                          }
                          // RAG desativado (2026-09-28)
                          // if (!KNOWLEDGE_BASE_PROVIDERS.includes(newProvider)) {
                          //   setFieldValue("knowledgeEnabled", false);
                          // }
                          loadModels(newProvider, values.apiKey);
                        }}
                      >
                        {providers.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.provider && errors.provider && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {errors.provider}
                        </div>
                      )}
                    </FormControl>

                    <FormControl fullWidth margin="dense" variant="outlined">
                      <Field
                        as={TextField}
                        label={`API Key`}
                        name="apiKey"
                        type={showApiKey ? "text" : "password"}
                        error={touched.apiKey && Boolean(errors.apiKey)}
                        helperText={touched.apiKey && errors.apiKey}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        required
                        autoComplete="new-password"
                        onBlur={(e) => {
                          handleBlur(e);
                          loadModels(values.provider, e.target.value);
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                                {showApiKey ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>

                    <Autocomplete
                      options={models}
                      disabled={!values.provider || !values.apiKey || modelsLoading || models.length === 0}
                      getOptionLabel={(option) => option.name}
                      getOptionSelected={(option, value) => option.id === value.id}
                      // Se a lista ainda não carregou (ou falhou), exibe o
                      // modelo salvo do nó em vez de um campo vazio.
                      value={
                        models.find((m) => m.id === values.model)
                          || (values.model ? { id: values.model, name: values.model } : null)
                      }
                      onChange={(e, value) => setFieldValue("model", value ? value.id : "")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Modelo"
                          variant="outlined"
                          margin="dense"
                          required
                          placeholder="Buscar modelo..."
                          error={touched.model && Boolean(errors.model)}
                          helperText={
                            (touched.model && errors.model)
                            || (modelsLoading && "Carregando modelos...")
                            || (!values.apiKey && "Informe a API Key para carregar os modelos")
                          }
                        />
                      )}
                    />
                    {modelsError && (
                      <Alert severity="error" style={{ marginTop: 8 }}>
                        {modelsError}
                      </Alert>
                    )}

                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} style={{ gap: 8 }}>
                      <input
                        ref={pdfInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        style={{ display: "none" }}
                        onChange={handlePromptPdfSelected}
                      />
                      <Tooltip
                        title={
                          values.provider && values.apiKey && values.model
                            ? "Lê o PDF e preenche o prompt seguindo a estrutura das Instruções"
                            : "Informe provedor, API Key e modelo para gerar a partir de um PDF"
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            startIcon={
                              generatingPrompt
                                ? <CircularProgress size={14} />
                                : <PictureAsPdf fontSize="small" />
                            }
                            disabled={!values.provider || !values.apiKey || !values.model || generatingPrompt}
                            onClick={() => pdfInputRef.current && pdfInputRef.current.click()}
                          >
                            {generatingPrompt ? "Gerando..." : "Gerar a partir de PDF"}
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        startIcon={<Info fontSize="small" />}
                        onClick={() => setInstructionsModalOpen(true)}
                      >
                        Instruções
                      </Button>
                    </Box>
                    {generatingPrompt && (
                      <Typography variant="caption" color="textSecondary">
                        Lendo o PDF e gerando o prompt... pode levar até 1 minuto.
                      </Typography>
                    )}
                    {promptGenError && (
                      <Alert severity="error" style={{ marginTop: 8 }} onClose={() => setPromptGenError("")}>
                        {promptGenError}
                      </Alert>
                    )}
                    {promptGenInfo && (
                      <Alert
                        severity={promptGenInfo.truncated ? "warning" : "info"}
                        style={{ marginTop: 8 }}
                        onClose={() => setPromptGenInfo(null)}
                      >
                        {promptGenInfo.truncated &&
                          "O resultado pode estar incompleto (o modelo atingiu o limite de texto). "}
                        Revise o prompt gerado
                        {promptGenInfo.placeholders > 0 &&
                          ` e complete ${promptGenInfo.placeholders === 1 ? "o trecho marcado" : `os ${promptGenInfo.placeholders} trechos marcados`} como [a definir]`}
                        . Depois clique em "{labels.btn}" aqui e em "Salvar" no fluxo — sem isso o prompt não é salvo.
                      </Alert>
                    )}
                    <Field
                      as={TextField}
                      label="Prompt do Agente de IA"
                      name="prompt"
                      error={touched.prompt && Boolean(errors.prompt)}
                      helperText={touched.prompt && errors.prompt}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      // RAG desativado (2026-09-28): era required={!(values.knowledgeEnabled && values.hasReadyKnowledgeFile)}
                      disabled={generatingPrompt}
                      rows={6}
                      multiline
                      placeholder="Descreva como a IA deve se comportar, que informações deve coletar, como deve responder..."
                    />

                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Temperatura"
                        name="temperature"
                        error={touched.temperature && Boolean(errors.temperature)}
                        helperText={touched.temperature && errors.temperature || "0 = conservador, 2 = criativo"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                        inputProps={{
                          step: "0.1",
                          min: "0",
                          max: "2",
                        }}
                      />
                      <Field
                        as={TextField}
                        label="Max Tokens"
                        name="maxTokens"
                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                        helperText={touched.maxTokens && errors.maxTokens || "Tamanho máximo da resposta"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                      <Field
                        as={TextField}
                        label="Max Mensagens"
                        name="maxMessages"
                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                        helperText={touched.maxMessages && errors.maxMessages || "Histórico de contexto"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                    </div>

                  </AccordionDetails>
                </Accordion>

                {/* CONFIGURAÇÕES DE VOZ — Gemini sempre responde em texto */}
                {/* Comentado à pedido do chefe */}
                {/* {values.provider !== "gemini" && (
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Chat />
                      Configurações de Voz (Opcional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.voice && Boolean(errors.voice)}
                    >
                      <InputLabel>Tipo de Resposta</InputLabel>
                      <Field
                        as={Select}
                        label="Tipo de Resposta"
                        name="voice"
                      >
                        {availableVoices.map((voice) => (
                          <MenuItem key={voice} value={voice}>
                            {getVoiceDisplayName(voice)}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.voice && errors.voice && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {errors.voice}
                        </div>
                      )}
                    </FormControl>

                    {values.voice !== "texto" && (
                      <div className={classes.multFieldLine}>
                        <Field
                          as={TextField}
                          label="Voice Key (Azure)"
                          name="voiceKey"
                          error={touched.voiceKey && Boolean(errors.voiceKey)}
                          helperText={touched.voiceKey && errors.voiceKey}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          placeholder="Chave do Azure Speech Service"
                        />
                        <Field
                          as={TextField}
                          label="Voice Region (Azure)"
                          name="voiceRegion"
                          error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                          helperText={touched.voiceRegion && errors.voiceRegion}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          placeholder="ex: brazilsouth"
                        />
                      </div>
                    )}

                  </AccordionDetails>
                </Accordion>
                )} */}

                {/* COMPORTAMENTO DO FLUXO */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Timer />
                      Comportamento do Fluxo
                      <Tooltip title="Configure como o OpenAI deve se comportar no fluxo">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl component="fieldset" margin="normal">
                      <FormLabel component="legend">Modo de Funcionamento</FormLabel>
                      <RadioGroup
                        name="flowMode"
                        value={values.flowMode}
                        onChange={(e) => setFieldValue("flowMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="permanent"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Permanente</strong> - Encerrar fluxo aqui
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                O usuário fica conversando com a IA até pedir transferência ou encerrar
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="temporary"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Temporário</strong> - Volta ao fluxo depois
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA executa uma tarefa específica e depois retorna ao fluxo normal
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* FILA DE TRANSFERÊNCIA DO MODO PERMANENTE */}
                    {values.flowMode === "permanent" && (
                      <div className={classes.temporarySettings}>
                        <Typography variant="h6" gutterBottom>
                          <SwapHoriz fontSize="small"  style={{ verticalAlign: "middle", marginRight: 4 }} />
                          Transferência para Atendimento
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Se o cliente pedir um atendente — ou a própria IA decidir transferir — o ticket
                          será movido para a fila escolhida abaixo, aguardando um atendente daquela fila.
                        </Typography>

                        <FormControl fullWidth margin="dense" variant="outlined">
                          <InputLabel>Fila de Transferência Padrão</InputLabel>
                          <Select
                            label="Fila de Transferência Padrão"
                            name="queueId"
                            value={values.queueId || 0}
                            onChange={(e) => setFieldValue("queueId", e.target.value)}
                          >
                            <MenuItem value={0}>
                              <em>Nenhuma — apenas encerra a IA</em>
                            </MenuItem>
                            {queues.map((queue) => (
                              <MenuItem key={queue.id} value={queue.id}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 9,
                                      height: 9,
                                      borderRadius: "50%",
                                      backgroundColor: queue.color
                                    }}
                                  />
                                  {queue.name}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="textSecondary">
                          Se nenhuma fila for selecionada, o ticket volta para "pendente" sem fila definida.
                        </Typography>
                      </div>
                    )}

                    {/* CONFIGURAÇÕES DO MODO TEMPORÁRIO */}
                    {values.flowMode === "temporary" && (
                      <div className={classes.temporarySettings}>
                        <Typography variant="h6" gutterBottom>
                          ⏱️ Configurações do Modo Temporário
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Configure quando a IA deve parar e retornar ao fluxo
                        </Typography>

                        {/* Limite de Interações */}
                        <Field
                          as={TextField}
                          label="Máximo de Interações"
                          name="maxInteractions"
                          error={touched.maxInteractions && Boolean(errors.maxInteractions)}
                          helperText={touched.maxInteractions && errors.maxInteractions || "Número máximo de mensagens antes de voltar ao fluxo (0 = ilimitado)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 50 }}
                        />

                        {/* Timeout */}
                        <Field
                          as={TextField}
                          label="Timeout (minutos)"
                          name="completionTimeout"
                          error={touched.completionTimeout && Boolean(errors.completionTimeout)}
                          helperText={touched.completionTimeout && errors.completionTimeout || "Tempo limite antes de voltar ao fluxo (0 = sem limite)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 60 }}
                        />

                        {/* Palavras-chave de Continuação */}
                        <FormControl fullWidth margin="dense">
                          <Typography variant="subtitle2" gutterBottom>
                            Palavras-chave para Continuar Fluxo
                          </Typography>
                          <FieldArray name="continueKeywords">
                            {(arrayHelpers) => (
                              <div>
                                <Box display="flex" gap={1} alignItems="center" mb={1}>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Digite uma palavra-chave"
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === "Enter") {
                                        addKeyword(arrayHelpers, newKeyword);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => addKeyword(arrayHelpers, newKeyword)}
                                    disabled={!newKeyword.trim()}
                                  >
                                    Adicionar
                                  </Button>
                                </Box>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {values.continueKeywords.map((keyword, index) => (
                                    <Chip
                                      key={index}
                                      label={keyword}
                                      className={classes.keywordChip}
                                      onDelete={() => removeKeyword(arrayHelpers, index)}
                                      deleteIcon={<Delete />}
                                      variant="outlined"
                                      size="small"
                                    />
                                  ))}
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                  Quando o usuário enviar uma dessas palavras, o fluxo continuará automaticamente
                                </Typography>
                                {touched.continueKeywords && errors.continueKeywords && (
                                  <Typography variant="caption" color="error">
                                    {errors.continueKeywords}
                                  </Typography>
                                )}
                              </div>
                            )}
                          </FieldArray>
                        </FormControl>

                        {/* Objetivo */}
                        <Field
                          as={TextField}
                          label="Objetivo da IA"
                          name="objective"
                          error={touched.objective && Boolean(errors.objective)}
                          helperText={touched.objective && errors.objective || "Descreva o que a IA deve completar (ex: 'Coletar nome, email e telefone')"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Ex: Coletar dados do cliente, Qualificar interesse, Diagnosticar problema..."
                        />

                        {/* Auto Completar */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.autoCompleteOnObjective}
                              onChange={(e) => setFieldValue("autoCompleteOnObjective", e.target.checked)}
                              name="autoCompleteOnObjective"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                Auto completar quando atingir objetivo
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA analisará automaticamente se completou o objetivo e voltará ao fluxo
                              </Typography>
                            </Box>
                          }
                        />

                        {/* Fila de Transferência */}
                        <Field
                          as={TextField}
                          label="ID da Fila (para transferência)"
                          name="queueId"
                          error={touched.queueId && Boolean(errors.queueId)}
                          helperText="ID da fila para onde transferir se usuário pedir atendente (0 = não transferir)"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0 }}
                        />

                      </div>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* BASE DE CONHECIMENTO (RAG) — RAG desativado (2026-09-28), substituída por
                    "Gerar a partir de PDF" no campo de prompt. Código mantido para uma
                    possível volta.
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <MenuBook />
                      Base de Conhecimento (RAG)
                      <Tooltip title="Anexe PDFs para a IA responder também com base neles">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {!KNOWLEDGE_BASE_PROVIDERS.includes(values.provider) ? (
                      <Alert severity="info">
                        A Base de Conhecimento está disponível apenas para OpenAI e Google
                        Gemini, que geram os embeddings dos PDFs com a mesma API Key do nó.
                        Com este provedor, o prompt do agente é obrigatório.
                      </Alert>
                    ) : (
                    <AiKnowledgeFilesUpload
                      flowId={flowId}
                      nodeId={data?.id}
                      knowledgeEnabled={values.knowledgeEnabled}
                      onChangeKnowledgeEnabled={(checked) => setFieldValue("knowledgeEnabled", checked)}
                      onReadyStateChange={(hasReady) => setFieldValue("hasReadyKnowledgeFile", hasReady)}
                      provider={values.provider}
                      apiKey={values.apiKey}
                    />
                    )}
                  </AccordionDetails>
                </Accordion>
                */}

              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCancel}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting || generatingPrompt}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      <AiPromptInstructionsModal
        open={instructionsModalOpen}
        onClose={() => setInstructionsModalOpen(false)}
      />
      <ConfirmationModal
        title="Substituir o prompt atual?"
        open={Boolean(pendingPromptPdf)}
        onClose={() => setPendingPromptPdf(null)}
        onConfirm={() => pendingPromptPdf && generatePromptFromPdf(pendingPromptPdf)}
      >
        O texto atual do prompt será substituído pelo gerado a partir do PDF.
      </ConfirmationModal>
    </div>
  );
};

export default FlowBuilderOpenAIModal;